import { Currency } from "@redotech/money/currencies";
import {
  FEDEX_TEST_ACCOUNT_INFO,
  FEDEX_TEST_INVOICE_DATA,
  SmartPostHub,
  TEST_FEDEX_PINS,
} from "@redotech/redo-fulfillment-carrier-account-common/fedex-customer-registration";
import { IDefaultCarrierCredentials } from "@redotech/redo-fulfillment-rate-common/create-carrier-account";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { nonEmptySingleErrorValidator } from "@redotech/ui/validators";

// FedEx only accepts test credentials in the sandbox environment - if we're in prod, do not use those. But when running locally use them by default
// const USE_SANDBOX_ACCOUNT_DEFAULTS = process.env.NODE_ENV !== "production";
const USE_SANDBOX_ACCOUNT_DEFAULTS = false; // Only useful for direct fedex integration, not MFA with easypost

export interface FedExParsedInvoiceAddress {
  name: string | null;
  streetLine1: string | null;
  streetLine2: string | null;
  city: string | null;
  stateOrProvinceCode: string | null;
  postalCode: string | null;
  countryCode: string | null;
}

export interface FedExParsedInvoiceAutofill {
  invoiceNumber: string | null;
  invoiceDate: string | null;
  paymentDueDate: string | null;
  accountNumber: string | null;
  accountName: string | null;
  invoiceAmount: string | null;
  invoiceCurrency: string | null;
  billingAddress: FedExParsedInvoiceAddress | null;
  shippingAddress: FedExParsedInvoiceAddress | null;
  invoiceSummary:
    | { line: string; amount: string | null; currency: string | null }[]
    | null;
  totalDiscount: string | null;
  totalCharges: string | null;
}

const getNormalizedAmountString = (
  value: string | null | undefined,
): string | undefined => {
  if (!value) {
    return undefined;
  }
  const normalized = value.replace(/[^0-9.,-]/g, "").replace(/,/g, "");
  return normalized || undefined;
};

const getNormalizedPlainDate = (
  value: string | null | undefined,
): Temporal.PlainDate | undefined => {
  if (!value) {
    return undefined;
  }
  try {
    return Temporal.PlainDate.from(value);
  } catch {
    return undefined;
  }
};

export const FedExAccountInput = groupInput({
  accountNumber: input<string>({ validator: nonEmptySingleErrorValidator }),
  customerName: input<string>({ validator: nonEmptySingleErrorValidator }),
  merchantFacingName: input<string>({
    validator: nonEmptySingleErrorValidator,
  }),
  streetLine1: input<string>({ validator: nonEmptySingleErrorValidator }),
  streetLine2: input<string>({ validator: undefined }),
  city: input<string>({ validator: nonEmptySingleErrorValidator }),
  stateOrProvinceCode: input<string>({
    validator: nonEmptySingleErrorValidator,
  }),
  postalCode: input<string>({ validator: nonEmptySingleErrorValidator }),
  countryCode: input<string>({ validator: nonEmptySingleErrorValidator }),
  smartPostHubId: input<SmartPostHub["hubId"] | undefined>({
    validator: undefined,
  }),
  residential: input<boolean>({ validator: undefined }),
});

export type FedExAccountValue = InputSchema.Value<typeof FedExAccountInput>;
export type FedExAccountForm = InputSchema.Input<typeof FedExAccountInput>;

export const fedexAccountDefaults = (
  defaultTeamInfo: IDefaultCarrierCredentials,
  parsedInvoice?: FedExParsedInvoiceAutofill | null,
): FedExAccountValue => {
  if (USE_SANDBOX_ACCOUNT_DEFAULTS) {
    return {
      accountNumber: FEDEX_TEST_ACCOUNT_INFO.accountNumber,
      customerName: FEDEX_TEST_ACCOUNT_INFO.customerName,
      merchantFacingName: "My FedEx Account",
      streetLine1: FEDEX_TEST_ACCOUNT_INFO.address.streetLines[0] ?? "",
      streetLine2: "",
      city: FEDEX_TEST_ACCOUNT_INFO.address.city,
      stateOrProvinceCode: FEDEX_TEST_ACCOUNT_INFO.address.stateOrProvinceCode,
      postalCode: FEDEX_TEST_ACCOUNT_INFO.address.postalCode,
      countryCode: FEDEX_TEST_ACCOUNT_INFO.address.countryCode,
      smartPostHubId: undefined,
      residential: false,
    };
  }
  const billingAddress = parsedInvoice?.billingAddress;
  return {
    accountNumber: parsedInvoice?.accountNumber ?? "",
    customerName: parsedInvoice?.accountName ?? "",
    merchantFacingName: "My FedEx Account",
    streetLine1: billingAddress?.streetLine1 ?? defaultTeamInfo.address_street,
    streetLine2: billingAddress?.streetLine2 ?? "",
    city: billingAddress?.city ?? defaultTeamInfo.address_city,
    stateOrProvinceCode:
      billingAddress?.stateOrProvinceCode ?? defaultTeamInfo.address_state,
    postalCode: billingAddress?.postalCode ?? defaultTeamInfo.address_zip,
    countryCode: billingAddress?.countryCode ?? defaultTeamInfo.address_country,
    smartPostHubId: undefined,
    residential: false,
  };
};

// MFA - Invoice
export const fedexMfaInvoiceInput = groupInput({
  invoiceNumber: input<string>({ validator: nonEmptySingleErrorValidator }),
  invoiceCurrency: input<string>({ validator: nonEmptySingleErrorValidator }),
  invoiceAmount: input<string>({ validator: nonEmptySingleErrorValidator }),
  invoiceDate: input<Temporal.PlainDate>({
    validator: (value) => {
      if (!value) {
        return ["Invoice date is required"];
      }
      const today = Temporal.Now.plainDateISO();
      const ninetyDaysAgo = today.subtract({ days: 90 });
      if (Temporal.PlainDate.compare(value, ninetyDaysAgo) < 0) {
        return ["Invoice date must be within the last 90 days"];
      }
      return [];
    },
  }),
});

export type FedexMfaInvoiceValue = InputSchema.Value<
  typeof fedexMfaInvoiceInput
>;
export type FedexMfaInvoiceForm = InputSchema.Input<
  typeof fedexMfaInvoiceInput
>;

export const fedexMfaInvoiceDefaults = (
  parsedInvoice?: FedExParsedInvoiceAutofill | null,
): FedexMfaInvoiceValue => {
  if (USE_SANDBOX_ACCOUNT_DEFAULTS) {
    return {
      invoiceNumber: FEDEX_TEST_INVOICE_DATA.number,
      invoiceCurrency: FEDEX_TEST_INVOICE_DATA.currency,
      invoiceAmount: FEDEX_TEST_INVOICE_DATA.amount,
      invoiceDate: Temporal.PlainDate.from(FEDEX_TEST_INVOICE_DATA.date),
    };
  }
  return {
    invoiceNumber: parsedInvoice?.invoiceNumber ?? "",
    invoiceCurrency: parsedInvoice?.invoiceCurrency ?? Currency.USD,
    invoiceAmount:
      getNormalizedAmountString(parsedInvoice?.invoiceAmount) ??
      getNormalizedAmountString(parsedInvoice?.totalCharges) ??
      "",
    invoiceDate:
      getNormalizedPlainDate(parsedInvoice?.invoiceDate) ??
      Temporal.Now.plainDateISO(),
  };
};

// MFA - Secure Code
export const fedexMfaSecureCodeInput = groupInput({
  secureCode: input<string>({ validator: nonEmptySingleErrorValidator }),
});

export type FedexMfaSecureCodeValue = InputSchema.Value<
  typeof fedexMfaSecureCodeInput
>;
export type FedexMfaSecureCodeForm = InputSchema.Input<
  typeof fedexMfaSecureCodeInput
>;

export const fedexMfaSecureCodeDefaults = (): FedexMfaSecureCodeValue => {
  if (USE_SANDBOX_ACCOUNT_DEFAULTS) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    return { secureCode: TEST_FEDEX_PINS[0] };
  }
  return { secureCode: "" };
};
